import { useState, useEffect, useCallback } from "react";
import { IonSelect, IonSelectOption } from "@ionic/react";
import { isUserLoggedIn, userHeaders } from "@/utils/userAuth";
import { APP_LIVE_EVENT } from "@/utils/appLive";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// The ACTIVE business's employees, one short-lived fetch shared by every
// select on a form; switching businesses (which emits the app-live event)
// refreshes it, since the employees belong to the business.
let cache = null; // { at, data: { hasAccess, employees } }
export async function fetchBusinessEmployees(force = false) {
  if (!isUserLoggedIn()) return { hasAccess: false, employees: [] };
  if (!force && cache && Date.now() - cache.at < 30000) return cache.data;
  try {
    const res = await fetch(`${BACKEND_URL}/api/user/employees`, { headers: userHeaders() });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || !d.success) throw new Error();
    const data = { hasAccess: !!d.hasAccess, employees: d.employees || [] };
    cache = { at: Date.now(), data };
    return data;
  } catch { return cache ? cache.data : { hasAccess: false, employees: [] }; }
}
export function invalidateBusinessEmployees() { cache = null; }

// A generator form's "Saved employee" dropdown, the saved-signature pattern:
// None first, picking one hands the whole employee to onApply, None hands
// null so the form can blank its employee fields. Renders nothing for
// guests, plans without the feature, or an empty list.
export default function SavedEmployeeSelect({ onApply, style, label = "Saved employee" }) {
  const [state, setState] = useState({ hasAccess: false, employees: [] });
  const [picked, setPicked] = useState("");

  const load = useCallback(async (force = false) => {
    const data = await fetchBusinessEmployees(force);
    setState(data);
    // a business switch swaps the list; a pick from the old one resets
    setPicked((p) => (p && !data.employees.some((e) => e.id === p) ? "" : p));
  }, []);
  useEffect(() => {
    load();
    const on = () => { invalidateBusinessEmployees(); load(true); };
    window.addEventListener(APP_LIVE_EVENT, on);
    return () => window.removeEventListener(APP_LIVE_EVENT, on);
  }, [load]);

  if (!state.hasAccess || state.employees.length === 0) return null;
  return (
    <IonSelect
      fill="outline" labelPlacement="floating" label={label} interface="popover"
      value={picked} style={style} className="saved-employee-select"
      onIonChange={(e) => {
        const id = e.detail.value;
        setPicked(id || "");
        if (!id) { onApply(null); return; }
        const emp = state.employees.find((x) => x.id === id);
        if (emp) onApply(emp);
      }}
    >
      <IonSelectOption value="">None</IonSelectOption>
      {state.employees.map((emp) => (
        <IonSelectOption key={emp.id} value={emp.id}>{emp.name}</IonSelectOption>
      ))}
    </IonSelect>
  );
}
